import PropTypes from 'prop-types';
import React from 'react';
import TextField from 'material-ui/TextField';
import _ from 'lodash';
import * as Colors from 'material-ui/styles/colors';
import DebounceInput from 'react-debounce-input';
import {FIELD_WIDTH} from '../consts';
import AbstractListerField from './AbstractListerField';

const TextFieldWithMaxLength = ({maxLength, value, id, ...props}) => {
  return (
    <div style={{position: 'relative', flexGrow: 1}}>
      <TextField id={id} value={value} {...props} />
      {maxLength && (
        <div
          id={`${id}-char-counter`}
          style={{position: 'absolute', color: Colors.grey500, right: 1, bottom: -10, fontSize: 12}}
        >
          {_.size(value)}/{maxLength}
        </div>
      )}
    </div>
  );
};

export default class ListerTextField extends AbstractListerField {
  state = {
    focus: false,
  };

  isNumeric(props = this.props) {
    const {type} = props;
    return type === 'number';
  }

  getValue(props = this.props) {
    let {value} = props;
    if (_.isPlainObject(value)) {
      value = value.value;
    } else if (_.isArray(value)) {
      return _.head(value);
    }
    return value;
  }

  handleFieldChange = (event, value, payload) => {
    let newValue = payload ? payload : value;
    if (newValue === undefined) {
      newValue = event.target.value;
    }
    if (this.isNumeric()) {
      if (_.isEmpty(newValue)) {
        newValue = 0;
      } else {
        const toNumber = _.toNumber(newValue);
        newValue = !_.isNaN(toNumber) ? toNumber : this.props.value.value;
      }
      if (newValue !== this.getValue()) {
        // this is specifically to handle '.' in a numeric field. setting the numerical value to the store will delete the dor.
        if (_.isObject(this.props.value)) {
          newValue = {...this.props.value, value: newValue};
        }
        this.updateFieldValue(newValue);
      }
    } else {
      this.updateFieldValue(newValue);
    }
  };

  handleKeyDown = event => {
    if (this.isNumeric()) {
      const charCodeNine = '9'.charCodeAt(0);
      if (
        event.keyCode > charCodeNine &&
        event.keyCode !== 190 &&
        event.keyCode !== 110 &&
        !(event.keyCode >= 96 && event.keyCode <= 105)
      ) {
        // anything blow 0 is non-display key
        event.preventDefault();
      } else if (event.keyCode === 190 || event.keyCode === 110) {
        const stringValue = _.toString(this.getValue());
        if (stringValue.indexOf('.') !== -1) {
          event.preventDefault();
        }
      }
    }
  };

  updateFieldValue = value => {
    const {onChange} = this.props;
    onChange(value);
  };

  renderSuffix = () => {
    const {value, feesMayApply, unit} = this.props;
    const suffix = unit || (_.isObject(value) ? value.unit || value.currencyID : '');
    if (feesMayApply || suffix) {
      const text = suffix + (feesMayApply ? ' (fees may apply)' : '');
      return <span style={{marginLeft: 5, marginRight: 10, color: Colors.grey400, fontWeight: 300}}>{text}</span>;
    }
    return null;
  };

  handleFocus = () => {
    const {onFocus} = this.props;
    if (onFocus) {
      onFocus(event);
    }
    this.setState({focus: true});
  };

  handleBlur = event => {
    const {onBlur} = this.props;
    if (onBlur) {
      onBlur(event);
    }
    this.setState({focus: false});
  };

  render() {
    const {focus} = this.state;
    const {zeroShown, width, style, id} = this.props;
    const value = this.getValue();
    const emptyText = zeroShown && !focus ? '0' : '';
    const suffix = this.renderSuffix();
    return (
      <div style={{display: 'flex', alignItems: 'baseline', width: suffix ? undefined : width}}>
        <DebounceInput
          id={id}
          style={{width, ...style}}
          fullWidth={width === FIELD_WIDTH.FULL}
          onBlur={this.handleBlur}
          onFocus={this.handleFocus}
          element={TextFieldWithMaxLength}
          onKeyDown={this.handleKeyDown}
          onChange={this.handleFieldChange}
          floatingLabelStyle={{width: 'max-content', whiteSpace: 'nowrap'}}
          {..._.pick(this.props, [
            'maxLength',
            'disabled',
            'readOnly',
            'floatingLabelText',
            'hintText',
            'multiLine',
            'errorStyle',
            'errorText',
          ])}
          value={value ? value : emptyText}
          debounceTimeout={250}
        />
        {suffix}
      </div>
    );
  }
}

ListerTextField.propTypes = {
  fieldName: PropTypes.string,
  onChange: PropTypes.func,
  floatingLabelText: PropTypes.string,
  feesMayApply: PropTypes.bool,
  required: PropTypes.bool,
  zeroShown: PropTypes.bool, // some numeric fields sholud show 0 and some should stay empty when the value is 0
  width: PropTypes.oneOf(_.values(FIELD_WIDTH)),
  maxLength: PropTypes.number,
};

ListerTextField.defaultProps = {
  width: FIELD_WIDTH.M,
};
